/* eslint max-statements: 0 */

import { createSelector } from 'reselect'
import { POPULAR_ACTIVITY_IDS_BY_SITE } from './constants'
import createFilterOptions from 'react-select-fast-filter-options'
import { selectSiteSlug } from 'services/site/selectors'

export const selectActivityTypes = (state) => state.activityTypes.types

export const sortActivityGroups = (a, b) => {
  if (a.popular && !b.popular) {
    return -1
  } else if (!a.popular && b.popular) {
    return 1
  } else {
    return a.label.localeCompare(b.label)
  }
}

export const selectActivityTypeOptions = createSelector(
  selectActivityTypes,
  selectSiteSlug,
  (activityTypes, site) => {
    const popularGroup = {
      label: 'Popular',
      options: [],
      popular: true,
      value: null,
      disabled: true,
    }

    const popularActivityIds = POPULAR_ACTIVITY_IDS_BY_SITE[site]

    const options = activityTypes.reduce((groupList, type) => {
      const isGroup = type.has_children
      const isPopular = popularActivityIds.indexOf(type._links.self[0].id) !== -1
      const selfId = type._links.self && type._links.self[0].id
      const groupId = type._links.parent && type._links.parent[0].id
      const parent = groupId && groupList.find(_type => _type.id === groupId)
      const group = isGroup && groupList.find(_type => _type.id === selfId)
      const belongsToNewGroup = !isGroup && groupId && !parent
      const isNewGroup = isGroup && !group
      const option = {
        id: selfId,
        label: type.name,
        value: selfId,
        options: isGroup ? [] : undefined,
      }

      // if it's a new group, initialize the list with the current type
      if (isNewGroup) {
        groupList.push({ ...option, options: [option] })
      }

      // if this type belongs to a new group, go ahead and initialize that group
      // with the id, and a list that includes the current type.
      // we'll fill in the label later, when we iterate to the group type itself
      if (belongsToNewGroup) {
        groupList.push({ id: groupId, options: [option] })
      } else if (parent) {
        // if this type belongs to a group already in the group list
        // just push it onto the options list
        parent.options.push(option)
      }

      // if the current type is a group, but it's not new: one of the children
      // came first and already initialized it, all we need to do is push the
      // group type, and update the label with the name
      if (isGroup && !isNewGroup) {
        group.label = type.name
        group.options.push(option)
      }

      // if this is a popular activity, push onto the popular group
      if (isPopular) {
        popularGroup.options.push({ id: selfId, label: type.name, popular: true, value: selfId })
      }

      return groupList
    }, [])

    // sort the regular groups alphabetically
    options.map(optionGroup => {
      if (optionGroup.options && optionGroup.options.length) {
        optionGroup.options.sort((a, b) => {
          return a.label.localeCompare(b.label)
        })
      }
    })

    // sort the popular ones based on site
    popularGroup.options.sort((a, b) => {
      const aIndex = popularActivityIds.indexOf(a.id)
      const bIndex = popularActivityIds.indexOf(b.id)
      return aIndex - bIndex
    })

    // add on popular groups
    options.push(popularGroup)

    // return a sorted list
    options.sort(sortActivityGroups)

    return options
  }
)

export const selectFlatActivityTypeOptions = createSelector(
  selectActivityTypeOptions,
  (activityTypeOptions) => {
    if (!activityTypeOptions || !activityTypeOptions.length) { return null }

    return activityTypeOptions.reduce((flatOptions, current) => {
      flatOptions.push({ ...current })

      if (current.options && current.options.length) {
        // push all the children...
        // ...unless the child is a group, we don't want to duplicate
        current.options.forEach((option) => {
          if (!option.options) { flatOptions.push({ ...option }) }
        })
      }

      return flatOptions
    }, [])
  }
)

export const selectFilterOptions = createSelector(
  selectFlatActivityTypeOptions,
  (options) => createFilterOptions({ options })
)

export const selectPopularActivityTypes = createSelector(
  selectActivityTypes,
  selectSiteSlug,
  (activityTypes, site) => {
    const popularActivityIds = POPULAR_ACTIVITY_IDS_BY_SITE[site]
    return activityTypes.filter((type) => {
      return popularActivityIds.indexOf(type._links.self[0].id) !== -1
    })
  }
)



// WEBPACK FOOTER //
// ./src/services/activityTypes/selectors.js